import { useEffect, useState, useRef } from 'react';
import { successAlert, warningAlert, errorAlert } from 'ui-component/Toasts';

// material-ui
import { Box, Button, Grid, Typography, TextField, FormControl, Select, MenuItem, Autocomplete } from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { gridSpacing } from 'app/constant';

import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { Link } from 'react-router-dom';
import { axiosInstance } from 'utils/axios';
import dayjs from 'dayjs';
import useFetchData from 'hooks/useFetchData';
import StickyHeadTable from './components/StickyHeadTable';
import * as XLSX from 'xlsx';
import { fontSize } from '@mui/system';
import { useTranslation } from "react-i18next";

const CustomerTasklistExpiration = () => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);
    const buttonRef = useRef(null);
    const triggeredByKeyRef = useRef(false);

    const [customerTasklist, setCustomerTasklist] = useState([]);
    const [expiredDateFrom, setExpiredDateFrom] = useState(dayjs().subtract(7, 'days'));
    const [expiredDateTo, setExpiredDateTo] = useState(dayjs().add(7, 'days'));

    const [loginUserName, setLoginUserName] = useState('');
    const [numberCode, setNumberCode] = useState('');
    const [statusID, setStatusID] = useState('1');

    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);

    const [fetchCustomerTasklistLoading, setFetchCustomerTasklistLoading] = useState(false);
    const fetchCustomers = async () => {
        setFilterBtnDisabled(true);

        if (triggeredByKeyRef.current) {
            triggeredByKeyRef.current = false; // Reset the flag
            return;
        }

        if (!expiredDateFrom.isValid() || !expiredDateTo.isValid()) {
            warningAlert('Please fill up all required fields.');
            return;
        }

        let fetchCustomersURL = `/CustomerTasklist/ExpiredDate/All?expiredDateFrom=${expiredDateFrom.format(
            'YYYY-MM-DD'
        )}&expiredDateTo=${expiredDateTo.format('YYYY-MM-DD')}`;

        if (loginUserName !== '') {
            fetchCustomersURL += `&loginUserName=${loginUserName}`;
        }
        if (numberCode !== '') {
            fetchCustomersURL += `&numberCode=${numberCode}`;
        }
        if (statusID !== '') {
            fetchCustomersURL += `&statusID=${statusID}`;
        }

        setFetchCustomerTasklistLoading(true);
        try {
            const res = await axiosInstance.get(fetchCustomersURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setCustomerTasklist(res.data);
                // setCustomers((customers) => [...customers, ...res.data]);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFetchCustomerTasklistLoading(false);
            setFilterBtnDisabled(false);
        }
    };
    
    const { data: customerTasklistStatus } = useFetchData('/CustomerTasklistStatus/All');

    useEffect(() => {
        Promise.all([fetchCustomers()]).catch((error) => {
            console.error('An error occurred:', error);
        });
    }, []);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            buttonRef.current.click(); // Simulate button click
        }
    };

    return (
        <Grid container item xs={12} onKeyDown={handleKeyDown} tabIndex={0}>
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>{t("customer.expiredDate")} :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs} style={{fontSize: '14px'}}>
                            <DatePicker
                                value={expiredDateFrom}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setExpiredDateFrom(newValue)}
                            />
                        </LocalizationProvider>
                        -
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={expiredDateTo}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setExpiredDateTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("customer.loginUserName")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={loginUserName}
                            onChange={(e) => setLoginUserName(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("customer.code")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={numberCode}
                            onChange={(e) => setNumberCode(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("customer.status")} :
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                displayEmpty
                                value={statusID}
                                onChange={(e) => setStatusID(e.target.value)}
                                sx={{
                                    fontSize: '13px',
                                    minHeight: '36px',
                                    '& .MuiSelect-select': {
                                        fontSize: '13px',
                                        padding: '10px 14px',
                                    },
                                    '& .MuiInputBase-root': {
                                        fontSize: '13px',
                                        minHeight: '36px',
                                        padding: '10px 14px',
                                    },
                                }}
                            >
                                <MenuItem value="">{t("customerTasklist.pleaseSelectCustomerTasklistStatus")}</MenuItem>
                                {customerTasklistStatus.map((item, index) => (
                                    <MenuItem value={item.id} key={index}>
                                        {item.name === 'Pending' ? t("customerTasklist.pending") 
                                        : item.name === 'Completed' ? t("customerTasklist.completed") 
                                        : item.name === 'Ready' ? t("customerTasklist.ready") 
                                        : item.name === 'Cancelled' ? t("customerTasklist.cancelled") 
                                        : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
            </Grid>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                    gap: '15px',
                    width: '50%',
                    marginLeft: 'auto'
                }}
            >
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500],
                        marginTop: '20px',
                        marginBottom: '20px'
                    }}
                    onClick={() => fetchCustomers()}
                    disabled={filterBtnDisabled}
                    ref={buttonRef}
                >
                    {t("customer.filter")}
                </Button>
            </Box>

            <StickyHeadTable
                customerTasklist={customerTasklist}
                fetchCustomerTasklist={fetchCustomers}
            />
        </Grid>
    );
};

export default CustomerTasklistExpiration;